import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { currentSessionId, sessionsApi } from '../../utils/deviceSession';
import { fetchLatestVersion } from '../../utils/appVersion';
import { deviceTitle } from '../../utils/deviceInfo';
import {
  buildPeople, summarize, filterPeople, filterCounts, checkup, placeOf, deviceMatchesQuery, FILTERS, STAFF_ROLES,
} from './sessionsUtils';
import PersonRow from './PersonRow';
import PasswordDialog from './PasswordDialog';
import ConfirmDialog from './ConfirmDialog';
import SecurityActivity from './SecurityActivity';
import './ActiveSessions.css';

/**
 * /sessions — owner only. The shop's security console, modelled on Google
 * Workspace / Microsoft 365 admin: every staff account with the devices it is
 * signed in on, a security checkup, search & filters, password + block
 * controls, device names, and an activity (audit) log.
 *
 * Big lists stay fast: people render PAGE_SIZE at a time ("Show more", the
 * admin-table pattern rather than infinite scroll), rows start collapsed once
 * there are more than a few, device details render only when opened, and the
 * Activity tab pages through Firestore on demand.
 *
 * Reads while open: one listener on `sessions`, one on staff `users` docs.
 */

export const PAGE_SIZE = 15;
const AUTO_OPEN_MAX = 3; // show everyone's devices when the list is this short
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function ActiveSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [latest, setLatest] = useState(null); // deployed build id, to flag old versions
  const [tab, setTab] = useState('people');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [role, setRole] = useState('all');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [openOverride, setOpenOverride] = useState({}); // uid → bool (user toggled)
  const [checkupOpen, setCheckupOpen] = useState(null); // null = automatic
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [pwFor, setPwFor] = useState(null);
  const searchRef = useRef(null);
  const mySession = currentSessionId();

  useEffect(() => onSnapshot(collection(db, 'sessions'), (snap) => {
    setSessions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoaded(true);
  }, (err) => {
    setLoaded(true);
    setError(err.code === 'permission-denied'
      ? 'Firestore rules for sessions are not deployed yet — run: firebase deploy --only firestore:rules'
      : `Could not load sessions: ${err.message}`);
  }), []);

  // Staff accounts, so people signed in nowhere still get password/block controls.
  useEffect(() => onSnapshot(query(collection(db, 'users'), where('role', 'in', STAFF_ROLES)), (snap) => {
    setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
  }, () => { /* sessions still render without it */ }), []);

  useEffect(() => {
    let live = true;
    fetchLatestVersion().then((v) => { if (live) setLatest(v); }).catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  // Search as you type, without re-filtering on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput), 150);
    return () => clearTimeout(t);
  }, [qInput]);

  // "/" jumps to search (GitHub / Gmail style) unless you're already typing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      e.preventDefault();
      setTab('people');
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // A new search / filter starts from the top with automatic expansion.
  useEffect(() => { setLimit(PAGE_SIZE); setOpenOverride({}); }, [q, filter, role]);

  const people = useMemo(() => buildPeople(users, sessions, now), [users, sessions, now]);
  const totals = useMemo(() => summarize(people, now), [people, now]);
  const counts = useMemo(() => filterCounts(people, now, latest), [people, now, latest]);
  const matches = useMemo(() => filterPeople(people, { q, filter, role }, now, latest), [people, q, filter, role, now, latest]);
  const visible = matches.slice(0, limit);
  const check = useMemo(() => checkup(people, now, latest, mySession), [people, now, latest, mySession]);
  const roles = useMemo(() => [...new Set(people.map((p) => p.role))].sort(), [people]);
  const attention = check.staleSessionIds.length + check.outdated + check.mustChange.length;
  const showCheckup = checkupOpen ?? attention > 0;

  const autoOpen = (p) => matches.length <= AUTO_OPEN_MAX || (!!q && p.sessions.some((s) => deviceMatchesQuery(s, q)));
  const isOpen = (p) => openOverride[p.uid] ?? autoOpen(p);
  const allOpen = visible.length > 0 && visible.every(isOpen);
  const setAll = (value) => setOpenOverride(Object.fromEntries(visible.map((p) => [p.uid, value])));

  const closeConfirm = useCallback(() => setConfirm(null), []);

  // ── Actions ────────────────────────────────────────────────────────────────
  const askAll = () => setConfirm({
    title: 'Log out all devices?',
    body: <>
      <p>Every account except yours is signed out everywhere — open devices right away, offline or older ones within an hour. Your other devices are signed out too; this one stays signed in.</p>
      <p>Use this after a security scare, or when a shared password may have leaked. Everyone will need to sign in again.</p>
    </>,
    cta: 'Log out all devices',
    typeToConfirm: 'LOG OUT',
    payload: { action: 'revoke-all', currentSessionId: mySession },
    done: (r) => `Signed out ${plural(r.accounts ?? 0, 'account')} (${plural(r.devices ?? 0, 'tracked device')}).`,
  });

  const askUser = (p) => {
    const self = p.uid === user?.uid;
    setConfirm({
      title: self ? 'Log out your other devices?' : `Log out ${p.name} everywhere?`,
      body: <p>{self
        ? 'All your other devices are signed out. This device stays signed in.'
        : `${p.name} is signed out on ${plural(p.activeCount, 'device')} (and any device not listed). They can sign in again with their password.`}</p>,
      cta: self ? 'Log out other devices' : 'Log out everywhere',
      payload: { action: 'revoke-user', uid: p.uid, currentSessionId: mySession },
      done: (r) => `Signed out ${plural(r.devices ?? 0, 'device')}.`,
    });
  };

  const askDevice = (p, s) => {
    const name = deviceTitle(s, p.name, { self: p.uid === user?.uid });
    setConfirm({
      title: `Log out ${name}?`,
      body: <p>{p.name} — {name} ({placeOf(s.location)}). If it is offline it signs out the next time it opens the app.</p>,
      cta: 'Log out device',
      payload: { action: 'revoke', sessionId: s.id },
      done: () => `${name} signed out.`,
    });
  };

  const askBlock = (p) => setConfirm(p.disabled ? {
    title: `Unblock ${p.name}?`,
    body: <p>{p.name} will be able to sign in again with their password.</p>,
    cta: 'Unblock',
    tone: 'primary',
    payload: { action: 'set-disabled', uid: p.uid, disabled: false },
    done: () => `${p.name} can sign in again.`,
  } : {
    title: `Block ${p.name} from signing in?`,
    body: <>
      <p>{p.name} is signed out everywhere and can’t sign in — even with the right password — until you unblock them.</p>
      <p>Use this when someone leaves, or a device with their login is lost. Their account and history are kept.</p>
    </>,
    cta: 'Block sign-in',
    payload: { action: 'set-disabled', uid: p.uid, disabled: true },
    done: (r) => `${p.name} is blocked${r.devices ? ` and signed out of ${plural(r.devices, 'device')}` : ''}.`,
  });

  const askStale = () => setConfirm({
    title: `Sign out ${plural(check.staleSessionIds.length, 'inactive device')}?`,
    body: <p>These devices haven’t opened the app in over 30 days — old phones, lost devices, or browsers that were never signed out. If one comes back it will have to sign in again.</p>,
    cta: 'Sign out inactive devices',
    payload: { action: 'revoke-sessions', sessionIds: check.staleSessionIds, currentSessionId: mySession },
    done: (r) => `Signed out ${plural(r.devices ?? 0, 'inactive device')}.`,
  });

  const run = async () => {
    if (!confirm || !auth.currentUser) return;
    setBusy(true);
    try {
      const r = await sessionsApi(auth.currentUser, confirm.payload, { timeoutMs: 30000 });
      setToast(confirm.done(r));
      setError('');
    } catch (err) {
      setError(err.message);
    }
    setConfirm(null);
    setBusy(false);
  };

  // Inline rename in a device's details; throws so the field can show the error.
  const renameDevice = async (s, name) => {
    const r = await sessionsApi(auth.currentUser, { action: 'rename-device', sessionId: s.id, name });
    setToast(r.name ? `Device renamed to “${r.name}”.` : 'Device name reset to automatic.');
  };

  const showFilter = (f) => { setTab('people'); setFilter(f); setQInput(''); setQ(''); setRole('all'); };
  const clearAll = () => { setQInput(''); setQ(''); setFilter('all'); setRole('all'); };
  const filtered = !!q || filter !== 'all' || role !== 'all';

  return (
    <div className="ses">
      <div className="ses__inner">
        <header className="ses__hero">
          <div>
            <p className="ses__eyebrow">Owner · Security</p>
            <h1 className="ses__title">Sessions &amp; security</h1>
            <p className="ses__sub">Who is signed in, on which devices, and what changed. Only you can see this page.</p>
          </div>
          <div className="ses__actions">
            <button type="button" className="ses__btn ses__btn--danger-solid" onClick={askAll} disabled={!loaded || busy}>
              ⎋ Log out all devices
            </button>
            <span className="ses__hint">Everyone except this device</span>
          </div>
        </header>

        {error && <div className="ses__error" role="alert">{error}</div>}

        <div className="ses__stats">
          <button type="button" className="ses__stat" onClick={() => showFilter('all')} aria-label="people signed in">
            <span className="ses__stat-n">{totals.people}</span><span className="ses__stat-l">people signed in</span>
          </button>
          <button type="button" className="ses__stat" onClick={() => showFilter('all')} aria-label="devices">
            <span className="ses__stat-n">{totals.devices}</span><span className="ses__stat-l">devices</span>
          </button>
          <button type="button" className="ses__stat" onClick={() => showFilter('online')} aria-label="online now">
            <span className="ses__stat-n ses__stat-n--green">{totals.online}</span><span className="ses__stat-l">online now</span>
          </button>
        </div>

        {loaded && (
          <section className="ses__checkup" aria-label="Security checkup">
            <button type="button" className="ses__checkup-head" aria-expanded={showCheckup} onClick={() => setCheckupOpen(!showCheckup)}>
              <span className="ses__section-title">🛡️ Security checkup</span>
              <span className={`ses__checkup-sum${attention ? ' is-warn' : ''}`}>
                {attention ? `${plural(attention, 'item')} to review` : check.blocked ? `${plural(check.blocked, 'blocked account')}` : 'All good'}
              </span>
              <span className={`ses__chev${showCheckup ? ' is-open' : ''}`} aria-hidden="true">›</span>
            </button>
            {showCheckup && (attention === 0 && !check.blocked ? (
              <p className="ses__check-ok">✅ Everything looks good — no stale devices, everyone is on the latest app, and no temporary passwords are pending.</p>
            ) : (
              <ul className="ses__check-list">
                {check.staleSessionIds.length > 0 && (
                  <li className="ses__check-item">
                    <span className="ses__check-icon" aria-hidden="true">🧹</span>
                    <div className="ses__check-text">
                      <b>{plural(check.staleSessionIds.length, 'device')} unused for 30+ days</b>
                      <span>Old phones or forgotten browsers that are still signed in.</span>
                    </div>
                    <button type="button" className="ses__btn ses__btn--sm" onClick={askStale} disabled={busy}>Sign them out</button>
                  </li>
                )}
                {check.outdated > 0 && (
                  <li className="ses__check-item">
                    <span className="ses__check-icon" aria-hidden="true">⬆️</span>
                    <div className="ses__check-text">
                      <b>{plural(check.outdated, 'device')} on an old app version</b>
                      <span>They update when the app is reopened. Logging a device out makes it sign in on the latest version.</span>
                    </div>
                    <button type="button" className="ses__btn ses__btn--sm" onClick={() => showFilter('outdated')}>Show</button>
                  </li>
                )}
                {check.mustChange.length > 0 && (
                  <li className="ses__check-item">
                    <span className="ses__check-icon" aria-hidden="true">🔑</span>
                    <div className="ses__check-text">
                      <b>{plural(check.mustChange.length, 'person')} still on a temporary password</b>
                      <span>{check.mustChange.map((p) => p.name).join(', ')} — they’ll be asked to choose their own at next sign-in.</span>
                    </div>
                  </li>
                )}
                {check.blocked > 0 && (
                  <li className="ses__check-item">
                    <span className="ses__check-icon" aria-hidden="true">🚫</span>
                    <div className="ses__check-text">
                      <b>{plural(check.blocked, 'account')} blocked</b>
                      <span>Can’t sign in until you unblock them.</span>
                    </div>
                    <button type="button" className="ses__btn ses__btn--sm" onClick={() => showFilter('blocked')}>Show</button>
                  </li>
                )}
              </ul>
            ))}
          </section>
        )}

        <div className="ses__tabs" role="tablist" aria-label="Sections">
          <button type="button" role="tab" aria-selected={tab === 'people'} className={`ses__tab${tab === 'people' ? ' is-on' : ''}`} onClick={() => setTab('people')}>
            People <span className="ses__tab-count">{people.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'activity'} className={`ses__tab${tab === 'activity' ? ' is-on' : ''}`} onClick={() => setTab('activity')}>
            Activity
          </button>
        </div>

        {tab === 'people' ? (
          <div role="tabpanel" aria-label="People">
            <div className="ses__sticky">
              <div className="ses__toolbar">
                <div className="ses__searchbox">
                  <span className="ses__searchicon" aria-hidden="true">⌕</span>
                  <input
                    ref={searchRef}
                    type="search"
                    className="ses__input ses__search"
                    placeholder="Search people, devices, cities…"
                    aria-label="Search people and devices"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') { setQInput(''); setQ(''); } }}
                  />
                  {qInput ? (
                    <button type="button" className="ses__clear" aria-label="Clear search" onClick={() => { setQInput(''); setQ(''); searchRef.current?.focus(); }}>×</button>
                  ) : (
                    <kbd className="ses__kbd" aria-hidden="true">/</kbd>
                  )}
                </div>
                <select className="ses__input ses__select" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="all">All roles</option>
                  {roles.map((r) => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
            </div>
            {/* Only search stays pinned: the chips would cover half a short screen. */}
            <div className="ses__chips" role="group" aria-label="Filter people">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" className={`ses__chip${filter === f.key ? ' is-on' : ''}`} aria-pressed={filter === f.key}
                  onClick={() => setFilter(f.key)} disabled={f.key !== 'all' && !counts[f.key] && filter !== f.key}>
                  {f.label} <span className="ses__chip-n">{counts[f.key]}</span>
                </button>
              ))}
            </div>
            <div className="ses__listbar">
              <span className="ses__count" aria-live="polite">
                {filtered ? `${matches.length} ${matches.length === 1 ? 'match' : 'matches'} of ${people.length}` : `${people.length} ${people.length === 1 ? 'person' : 'people'}`}
                {filtered && <button type="button" className="ses__linkbtn" onClick={clearAll}>Clear</button>}
              </span>
              {visible.length > 0 && (
                <button type="button" className="ses__linkbtn" onClick={() => setAll(!allOpen)}>
                  {allOpen ? 'Collapse all' : 'Expand all'}
                </button>
              )}
            </div>

            {!loaded && <div className="ses__skeleton" aria-hidden="true"><span /><span /><span /></div>}
            {loaded && !matches.length && (
              <div className="ses__empty">
                {people.length ? 'No one matches these filters.' : 'No one has checked in yet. People appear the next time they open the app.'}
                {filtered && <button type="button" className="ses__linkbtn" onClick={clearAll}>Clear filters</button>}
              </div>
            )}

            <div className="ses__list">
              {visible.map((p) => (
                <PersonRow
                  key={p.uid}
                  person={p}
                  self={p.uid === user?.uid}
                  open={isOpen(p)}
                  onToggle={() => setOpenOverride((o) => ({ ...o, [p.uid]: !isOpen(p) }))}
                  now={now}
                  latest={latest}
                  mySession={mySession}
                  busy={busy}
                  q={q}
                  onLogoutUser={() => askUser(p)}
                  onLogoutDevice={(s) => askDevice(p, s)}
                  onPassword={() => setPwFor(p)}
                  onBlock={() => askBlock(p)}
                  onRename={renameDevice}
                />
              ))}
            </div>

            {matches.length > visible.length && (
              <div className="ses__more">
                <span>Showing {visible.length} of {matches.length}</span>
                <button type="button" className="ses__btn" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                  Show {Math.min(PAGE_SIZE, matches.length - visible.length)} more
                </button>
                <button type="button" className="ses__linkbtn" onClick={() => setLimit(matches.length)}>Show all</button>
              </div>
            )}

            {loaded && !error && (
              <p className="ses__foot">
                Tap a person to see their devices, and a device for details or to rename it. Browsers never reveal a phone’s own name,
                so devices are named after their owner (“Ravi’s iPhone”) until you rename them. Press <kbd className="ses__kbd">/</kbd> to search.
              </p>
            )}
          </div>
        ) : (
          <div role="tabpanel" aria-label="Activity">
            <SecurityActivity now={now} />
          </div>
        )}
      </div>

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          body={confirm.body}
          cta={confirm.cta}
          tone={confirm.tone}
          typeToConfirm={confirm.typeToConfirm}
          busy={busy}
          onConfirm={run}
          onCancel={closeConfirm}
        />
      )}

      {pwFor && <PasswordDialog person={pwFor} onClose={() => setPwFor(null)} />}

      {toast && <div className="ses__toast" role="status">{toast}</div>}
    </div>
  );
}
